import React, { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { CoachCard } from "~/components/coach-card";
import { FormField } from "~/components/ui/form-field";
import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { Controller } from "react-hook-form";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { CountrySelect } from "~/components/country-select";
import { LanguageSelect } from "~/components/language-select";
import { CoachCardSkeleton } from "~/components/coach-card-skeleton";
import { CustomMultiSelect } from "~/components/ui/custom-multi-select";
import ServerErrorGraphic from "~/img/server-error.svg";
import NotFoundGraphic from "~/img/not-found-telescope.svg";

import { cn } from "~/lib/cn";
import { authMiddleware } from "~/lib/auth-middleware";
import { getUserPreferences } from "~/lib/functions/user";

import {
  CoachDiscoverySchema,
  coachAgesField,
  coachGenderField,
  coachLocationCountryField,
  coachLocationRegionField,
  coachPersonalityField,
  coachingApproachesField,
  coachingFormatsFields,
  coachingPackagesField,
  faithIntegrationsField,
  goalsField,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
} from "~/lib/schemas/coach-discovery";

import type { CoachDiscoveryForm } from "~/lib/schemas/coach-discovery";

import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";
import type { CoachMatchPreferences } from "~/lib/matching";
import { getCoachMatches } from "~/lib/matching";
import { Button } from "~/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const getFormData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const userPreferences = await getUserPreferences({
      data: { userId: context.user.id },
    });
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  });

export const getCoaches = createServerFn({ method: "POST" })
  .validator((data: unknown) => data as Partial<CoachDiscoveryForm>)
  .handler(async ({ data: values, context: _context }) => {
    const embeddingPrompt = `
    This user is seeking a coach with the following preferences:
    
    Primary Coaching Needs:
    - Main goals: ${values.goals?.join(", ") || "Not specified"}
    - Other goals: ${values.otherGoal || "Not specified"}

    Coaching Format & Delivery:
    - Coaching Format: ${values.coachingFormats || "Not specified"}
    - Session Format: ${values.sessionDeliveryMethods || "Not specified"}

    Experience Level & Coaching Packages:
    - Coach Experience Levels: ${values.coachingPackages?.join(", ") || "Not specified"}

    Coaching Style & Approach
    - Coaching Approaches: ${values.coachingApproaches?.join(", ") || "Not specified"}
    
    Coach Characteristics Preferences:
    - Preferred coach gender: ${values.coachGender || "No preference"}
    - Faith/religion alignment: ${values.faithIntegration?.join(", ") || "Not specified"}
    - Language requirements: ${values.preferredLanguages?.join(", ") || "Not specified"}
    - Coach Ages: ${values.coachAges?.join(", ") || "Not specified"}
    
    Location & Logistics:
    - Prefers coach location: ${values.prefersCoachLocation}
    - Coach location region: ${values.coachLocationRegion || "Not specified"}
    - Coach location country: ${values.coachLocationCountry || "Not specified"}
    
    Additional Context:
    - Other faith preference: ${values.otherFaithIntegration || "Not specified"}
    `;

    // Dynamic imports for server-only modules
    const [{ getAIPreferencesGenerator }, { serverEnv }] = await Promise.all([
      import("~/lib/ai-preferences-generator"),
      import("~/lib/server-env"),
    ]);

    const AiPreferencesGenerator = getAIPreferencesGenerator(
      serverEnv.EMBEDDINGS_OPENAI_API_KEY!,
      serverEnv.EMBEDDINGS_OPENAI_BASE_URL!,
    );

    const embedding =
      await AiPreferencesGenerator.createEmbedding(embeddingPrompt);

    const preferences: CoachMatchPreferences = {
      ...values,
      goals: values.goals || [],
      personalContext: values.personalContext || "",
      sessionDeliveryMethods: values.sessionDeliveryMethods || "No preference",
      prefersCoachLocation: values.prefersCoachLocation || "No preference",
      coachGender: values.coachGender || "No preference",
      coachingFormats: values.coachingFormats || "any",
      startTime: values.startTime || "I'm flexible",
      support: values.support || "Not sure yet – I'd like guidance on this",
      preferredLanguages: values.preferredLanguages || [],
      faithIntegration: values.faithIntegration || [],
      coachAges: values.coachAges || [],
      coachLocationRegion: values.coachLocationRegion || "No preference",
      coachLocationCountry: values.coachLocationCountry || "No preference",
      otherFaithIntegration: values.otherFaithIntegration || "No preference",
      coachPersonality: values.coachPersonality || [],
      userPersonality: values.userPersonality || [],
      coachingPackages: values.coachingPackages || [],
      coachingApproaches: values.coachingApproaches || [],
      embedding,
    };

    return await getCoachMatches({
      data: {
        preferences,
        limit: 24,
        queryLimit: 36,
      },
    });
  });

export const Route = createFileRoute("/_user/coach-discovery/")({
  head: () =>
    createNoIndexMeta(
      "Find A Coach – Dream Coach Match",
      "Discover your perfect coach with our advanced matching system",
    ),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["coach-discovery-form"],
      queryFn: () => getFormData(),
    });
    return {};
  },
  component: CoachDiscoveryPage,
});

function CoachDiscoveryPage() {
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  const { data: initialFormData } = useQuery({
    queryKey: ["coach-discovery-form"],
    queryFn: () => getFormData(),
  });

  const {
    register,
    handleSubmit: _handleSubmit,
    watch,
    setValue,
    getValues,
    formState,
    control,
  } = useForm<CoachDiscoveryForm>({
    resolver: zodResolver(CoachDiscoverySchema),
    defaultValues: initialFormData,
  });

  // Watch all relevant form fields that affect coach matching
  const watchedFields = watch([
    "goals",
    "coachingFormats",
    "sessionDeliveryMethods",
    "prefersCoachLocation",
    "coachLocationCountry",
    "coachLocationRegion",
    "preferredLanguages",
    "coachGender",
    "coachAges",
    "coachingPackages",
    "coachingApproaches",
    "coachPersonality",
    "faithIntegration",
  ]);

  // Memoize form values object to prevent unnecessary re-renders
  const formValues = useMemo(() => {
    const [
      goals,
      coachingFormats,
      sessionDeliveryMethods,
      prefersCoachLocation,
      coachLocationCountry,
      coachLocationRegion,
      preferredLanguages,
      coachGender,
      coachAges,
      coachingPackages,
      coachingApproaches,
      coachPersonality,
      faithIntegration,
    ] = watchedFields;
    return {
      goals,
      coachingFormats,
      sessionDeliveryMethods,
      prefersCoachLocation,
      coachLocationCountry,
      coachLocationRegion,
      preferredLanguages,
      coachGender,
      coachAges,
      coachingPackages,
      coachingApproaches,
      coachPersonality,
      faithIntegration,
    };
  }, [watchedFields]);

  const [debouncedPreferences, setDebouncedPreferences] = useState<
    Partial<CoachDiscoveryForm>
  >(initialFormData || {});

  // Debounce form changes with more efficient comparison
  useEffect(() => {
    if (!formValues || 0 === Object.keys(formValues).length) {
      return;
    }

    // Convert both to strings for comparison to avoid type issues
    if (JSON.stringify(formValues) === JSON.stringify(debouncedPreferences)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedPreferences(formValues);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [formValues, debouncedPreferences]);

  const {
    data: coaches,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["coaches", debouncedPreferences],
    queryFn: () => {
      const dataToSend =
        0 < Object.keys(debouncedPreferences).length
          ? debouncedPreferences
          : initialFormData || {};

      // Ensure required fields are present with defaults
      const completeData = {
        goals: [],
        coachingFormats: "any",
        sessionDeliveryMethods: "No preference",
        prefersCoachLocation: "No preference",
        coachGender: "No preference",
        faithIntegration: [],
        preferredLanguages: [],
        coachAges: [],
        coachingPackages: [],
        coachingApproaches: [],
        coachPersonality: [],
        ...dataToSend,
      };

      return getCoaches({ data: completeData });
    },
    enabled: !!debouncedPreferences,
  });

  const renderCoachesContent = () => {
    if (isLoading) {
      return (
        <div className="grow-3 grid basis-0 grid-cols-1 items-start justify-stretch gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={`skeleton-${i}`}>
              <CoachCardSkeleton
                className="animate-in fade-in w-full grow"
                seed={i}
              />
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="grow-3 flex min-h-[80vh] w-full basis-0 flex-col items-center justify-center gap-4">
          <h1 className="font-heading mt-12 text-center text-3xl font-medium tracking-tight">
            That didn't work
          </h1>
          <p className="text-text-secondary mx-auto max-w-md text-center">
            We're sorry, something went wrong finding coaches for you. Try
            refreshing the page or changing your filters.
          </p>
          <div className="max-h-sm mx-auto aspect-square max-w-sm grow-0">
            <ServerErrorGraphic />
          </div>
        </div>
      );
    }

    if (!coaches || 0 === coaches.length) {
      return (
        <div className="grow-3 flex min-h-[80vh] w-full basis-0 flex-col items-center justify-center gap-4">
          <h1 className="font-heading mt-12 text-center text-3xl font-medium tracking-tight">
            No Dream Coach for You...Yet
          </h1>
          <p className="text-text-secondary mx-auto max-w-md text-center">
            We looked hard but couldn't find a Dream Coach for you.
          </p>
          <p className="text-text-secondary mx-auto max-w-md text-center">
            You can change your selection criteria and try again.
          </p>
          <div className="max-h-sm mx-auto aspect-square max-w-sm grow-0">
            <NotFoundGraphic />
          </div>
        </div>
      );
    }

    return (
      <div className="grow-3 grid basis-0 grid-cols-1 items-start justify-stretch gap-4 md:grid-cols-2 lg:grid-cols-3">
        {coaches.map((data, index) => (
          <CoachCard
            key={data.coach.id}
            coachData={data}
            className="animate-in fade-in h-96"
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="relative mx-auto mb-8 flex w-full max-w-screen-xl flex-col overflow-visible px-4 xl:px-0">
      <div className="mb-2 mt-4 flex justify-between">
        <Breadcrumb
          items={[{ label: "Find A Coach", href: "/coach-discovery/" }]}
          size="3xl"
          isHeading
          variant="chevrons"
          useGrayLinks={false}
        />
      </div>
      <div className="flex items-start justify-stretch gap-4">
        <Card className="w-100 grow-1 h-fit max-w-full basis-0 p-4 shadow-none hover:shadow-none">
          <h3 className="font-heading text-2xl">Filters</h3>

          <form className="w-full space-y-8">
            {/* Goals to Achieve */}
            <div className="mt-4">
              <FormField label="Goals" error={formState.errors.goals?.message}>
                <CheckboxGroupField
                  name="goals"
                  control={control}
                  options={
                    goalsField.options?.filter(
                      ({ value }) => "Other" !== value,
                    ) || []
                  }
                />
              </FormField>
            </div>

            {/* Coaching Formats */}
            <FormField
              label={coachingFormatsFields.subTitle || ""}
              error={formState.errors.coachingFormats?.message}
            >
              <ControlledRadioGroup
                control={control}
                name="coachingFormats"
                options={
                  coachingFormatsFields.options?.map(({ value, label }) => ({
                    value,
                    label,
                  })) || []
                }
              />
            </FormField>

            {/* Session Delivery Methods */}
            <FormField
              label={sessionDeliveryMethodsFeild.subTitle || ""}
              error={formState.errors.sessionDeliveryMethods?.message}
            >
              <ControlledRadioGroup
                control={control}
                name="sessionDeliveryMethods"
                options={
                  sessionDeliveryMethodsFeild.options?.map(
                    ({ value, label }) => ({
                      value,
                      label,
                    }),
                  ) || []
                }
              />
            </FormField>

            {/* Location */}
            <FormField
              label={prefersCoachLocationField.subTitle || ""}
              error={formState.errors.prefersCoachLocation?.message}
            >
              <Controller
                control={control}
                name="prefersCoachLocation"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={
                          prefersCoachLocationField?.options?.[0]?.value || ""
                        }
                        id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                      />
                      <label
                        htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {prefersCoachLocationField?.options?.[0]?.label || ""}
                      </label>
                    </div>
                    {/* Conditional Location Details */}
                    <div
                      className={cn(
                        "max-h-0 overflow-hidden opacity-0 transition-all duration-700",
                        {
                          "max-h-screen opacity-100": "Yes" === field.value,
                        },
                      )}
                    >
                      <div className="mt-4 flex flex-col gap-4 px-1">
                        <FormField
                          label={coachLocationCountryField.subTitle}
                          error={formState.errors.coachLocationCountry?.message}
                        >
                          <Controller
                            control={control}
                            name="coachLocationCountry"
                            render={({ field: countryField }) => (
                              <CountrySelect
                                value={countryField.value}
                                onChange={countryField.onChange}
                              />
                            )}
                          />
                        </FormField>
                        <FormField
                          label={coachLocationRegionField.subTitle || ""}
                          error={formState.errors.coachLocationRegion?.message}
                        >
                          <Input
                            {...register("coachLocationRegion")}
                            className="px-1"
                            error={
                              formState.errors.coachLocationRegion?.message
                            }
                          />
                        </FormField>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={
                          prefersCoachLocationField?.options?.[1]?.value || ""
                        }
                        id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                      />
                      <label
                        htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {prefersCoachLocationField?.options?.[1]?.label ?? ""}
                      </label>
                    </div>
                  </RadioGroup>
                )}
              />
            </FormField>

            {/* Preferred Languages */}
            <FormField
              label="Language"
              error={formState.errors.preferredLanguages?.message}
            >
              <LanguageSelect
                field={{
                  value: getValues("preferredLanguages"),
                  name: "preferredLanguages",
                }}
                props={register("preferredLanguages")}
                variant="slim"
                placeholder=""
              />
            </FormField>

            {/* Coach Gender */}
            <FormField
              label="Coach gender"
              error={formState.errors.coachGender?.message}
            >
              <ControlledRadioGroup
                control={control}
                name="coachGender"
                options={
                  coachGenderField.options?.map(({ value, label }) => ({
                    value,
                    label,
                  })) || []
                }
              />
            </FormField>

            {/* Coach Age */}
            <FormField
              label="Coach ages"
              error={formState.errors.coachAges?.message}
            >
              <CustomMultiSelect
                field={{
                  value: getValues("coachAges") || [],
                  name: "coachAges",
                }}
                props={register("coachAges")}
                placeholder=""
                variant="slim"
                items={coachAgesField.options || []}
                exclusiveValues={["No preference"]}
              />
            </FormField>

            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
            >
              {isAdvancedExpanded ? (
                <>
                  Hide advanced filters <ChevronUp className="size-[1em]" />
                </>
              ) : (
                <>
                  Show advanced filters <ChevronDown className="size-[1em]" />
                </>
              )}
            </Button>

            <div
              className={cn(
                "flex flex-col items-stretch space-y-8 opacity-0 transition-opacity",
                {
                  "opacity-100": isAdvancedExpanded,
                },
              )}
            >
              {/* Coaching Experience Level */}
              <FormField
                label="Coaching experience levels"
                error={formState.errors.coachingPackages?.message}
              >
                <CustomMultiSelect
                  field={{
                    value: getValues("coachingPackages") || [],
                    name: "coachingPackages",
                  }}
                  props={register("coachingPackages")}
                  placeholder=""
                  variant="slim"
                  items={coachingPackagesField.options || []}
                  exclusiveValues={["No preference"]}
                />
              </FormField>

              {/* Coaching Style */}
              <FormField
                label={"Coaching Style"}
                error={formState.errors.coachingApproaches?.message}
              >
                <CustomMultiSelect
                  field={{
                    value: getValues("coachingApproaches") || [],
                    name: "coachingApproaches",
                  }}
                  props={register("coachingApproaches")}
                  placeholder=""
                  variant="slim"
                  exclusiveValues={["Not sure yet"]}
                  items={coachingApproachesField.options || []}
                />
              </FormField>

              {/* Coach Personality */}
              <div className="mt-8">
                <FormField
                  labelSize="sm"
                  error={formState.errors.coachPersonality?.message}
                  label="Preferred coach personality traits"
                >
                  <CheckboxGroupField
                    name="coachPersonality"
                    control={control}
                    options={coachPersonalityField.options || []}
                    exclusiveOptions={["Skip this question"]}
                  />
                </FormField>
              </div>

              {/* Faith Integration */}
              <FormField
                label="Faith preferences"
                error={formState.errors.faithIntegration?.message}
              >
                <CheckboxGroupField
                  name="faithIntegration"
                  control={control}
                  options={
                    faithIntegrationsField.options.filter(
                      ({ value }) => "Other" !== value,
                    ) || []
                  }
                  exclusiveOptions={["No preference"]}
                  onTrigger={{
                    "No preference": () =>
                      setValue("otherFaithIntegration", undefined),
                  }}
                />
              </FormField>
            </div>
          </form>
        </Card>

        {renderCoachesContent()}
      </div>
    </div>
  );
}
